import { type DataSourceAbstract, DataSourceType, FilterMode } from '@lighthouse/core'
import {
    getAppointField,
    getIsFindUseInFilter,
    getIsFindUseInSort,
    getViewColumns,
    notDataSettingInnerTypes,
    useAtomAction,
    useAtomData,
    useFindUseObjectContext,
    ViewFieldFilterConfigure,
    ViewSortConfigure
} from '@lighthouse/shared'
import React, { useCallback, useDeferredValue, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import {
    availableFieldBlocksAtom,
    availableFilterBlocksAtom,
    blockHighlightAtom,
    lastPageOfStackAtom,
    pagePathParameterFamily,
    pageQueryParameterFamily,
    pageStatesParameterFamily,
    pageVariableSchemasFamily
} from '@/atoms/page/state'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'

import { ListItemMenu } from '../Common/ListItemMenu'

interface DataSettingProps {
    prefixName: string
    dataSource: DataSourceAbstract
}

export const DataSetting: React.FC<DataSettingProps> = ({ prefixName, dataSource }) => {
    const disabledWithVersion = useIsDisabledWithVersion()
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => s?.pageId || '', [])
    )
    const pageVariables = useAtomData(pageVariableSchemasFamily(pageId))
    const pagePathParameter = useAtomData(pagePathParameterFamily(pageId))
    const pageQueryParameter = useAtomData(pageQueryParameterFamily(pageId))
    const pageStateParameter = useAtomData(pageStatesParameterFamily(pageId))
    const availableFieldBlocksInfo = useDeferredValue(useAtomData(availableFieldBlocksAtom))
    const availableFilterBlocksInfo = useDeferredValue(useAtomData(availableFilterBlocksAtom))
    const { run: setBlockHighlight } = useAtomAction(blockHighlightAtom)

    const handleMouseEnter = useCallback(
        (id: string) => {
            setBlockHighlight([id])
            document.querySelector(`div[data-node-id="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        },
        [setBlockHighlight]
    )

    const handleMouseLeave = useCallback(() => {
        setBlockHighlight([])
    }, [setBlockHighlight])

    const { control } = useFormContext()
    const findUseObject = useFindUseObjectContext()
    const {
        id: dsId,
        viewOptions: { tableProps },
        schema
    } = dataSource
    const columns = getViewColumns({
        tableProps,
        schema
    })

    const primaryField = useMemo(() => {
        if (!dataSource) {
            return columns[0]?.fieldId
        }
        const field = getAppointField(dataSource, 'ID')
        return field?.id || columns[0]?.fieldId
    }, [columns, dataSource])

    return (
        <>
            <Controller
                name={`${prefixName}.filter`}
                control={control}
                render={({ field: { onChange, value } }) => {
                    const filter = value
                    const filtersLength = filter?.expression?.conditions?.length || 0
                    const isHighLight = getIsFindUseInFilter({
                        findUseObject,
                        data: filter,
                        currentDsId: dsId
                    })
                    return (
                        <ListItemMenu
                            width="auto"
                            highlighting={isHighLight}
                            enableHover={false}
                            label="筛选数据"
                            positionDependencies={[filter]}
                            value={Boolean(filtersLength) && `${String(filtersLength)} 条筛选项`}
                            popupContent={
                                <ViewFieldFilterConfigure
                                    dataSource={dataSource}
                                    dataSourceList={dataSourceList}
                                    pagePathParameter={pagePathParameter}
                                    pageQueryParameter={pageQueryParameter}
                                    pageVariables={pageVariables}
                                    pageStateParameter={pageStateParameter}
                                    availableFieldBlocksInfo={availableFieldBlocksInfo}
                                    availableFilterBlocksInfo={availableFilterBlocksInfo}
                                    columns={columns}
                                    primaryField={primaryField}
                                    filter={filter}
                                    fieldMode={FilterMode.CUSTOM}
                                    paramsMode={FilterMode.CUSTOM}
                                    noSettingInnerType={notDataSettingInnerTypes}
                                    disabled={disabledWithVersion}
                                    onFilter={onChange}
                                    onMouseEnter={handleMouseEnter}
                                    onMouseLeave={handleMouseLeave}
                                />
                            }
                        />
                    )
                }}
            />

            <Controller
                name={`${prefixName}.sorts`}
                control={control}
                render={({ field: { onChange, value } }) => {
                    const isHighLight = getIsFindUseInSort({
                        findUseObject,
                        data: value,
                        currentDsId: dsId
                    })
                    return (
                        <ListItemMenu
                            width="auto"
                            highlighting={isHighLight}
                            enableHover={false}
                            label="排序数据"
                            positionDependencies={[value]}
                            value={Boolean(value?.length) && `${String(value?.length)} 条排序`}
                            popupContent={
                                <ViewSortConfigure
                                    // noSettingFields={viewNotFilterSettingFieldTypes}
                                    columns={columns}
                                    primaryField={primaryField}
                                    sorters={value}
                                    disabled={disabledWithVersion}
                                    isShowDsName={dataSource.type === DataSourceType.joinDataSource}
                                    onChangeSorter={onChange}
                                />
                            }
                        />
                    )
                }}
            />
        </>
    )
}
